import * as Headless from '@headlessui/react';
import React from 'react';
import clsx from 'clsx';

interface RadioGroupProps {
  className?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  name?: string;
  children?: React.ReactNode;
}

export function RadioGroup({
  className = '',
  value,
  onChange,
  disabled,
  name,
  children
}: RadioGroupProps) {
  return (
    <Headless.RadioGroup
      data-slot="control"
      value={value}
      onChange={onChange}
      disabled={disabled}
      name={name}
      className={clsx('flex gap-4', className)}
    >
      {children}
    </Headless.RadioGroup>
  );
}

export function RadioField({
  className = '',
  ...props
}: {
  className?: string;
} & Headless.FieldProps) {
  return (
    <Headless.Field
      data-slot="field"
      {...props}
      className={clsx(
        'flex items-center gap-2 cursor-pointer',
        className
      )}
    />
  );
}

interface RadioProps {
  className?: string;
  value: string;
  disabled?: boolean;
}

export function Radio({
  className = '',
  disabled = false,
  value
}: RadioProps) {
  return (
    <Headless.Radio
      data-slot="control"
      disabled={disabled}
      value={value}
      className={clsx(
        // Base styles
        'relative inline-flex items-center justify-center w-5 h-5',
        'border-2 border-zinc-300 rounded-full bg-white cursor-pointer',
        'transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
        // Hover states
        'hover:border-primary/50',
        // Checked state via data attribute
        'data-[checked]:border-primary',
        // Disabled states
        disabled && 'opacity-50 pointer-events-none',
        className
      )}
    >
      {({ checked }) => (
        <span
          className={clsx(
            'w-2.5 h-2.5 rounded-full bg-primary transition-transform duration-200',
            checked ? 'scale-100' : 'scale-0'
          )}
          data-slot="indicator"
        />
      )}
    </Headless.Radio>
  );
}

export function RadioLabel({
  className = '',
  ...props
}: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <Headless.Label
      {...props}
      className={clsx(
        'text-sm font-medium text-gray-700 cursor-pointer select-none',
        className
      )}
    />
  );
}
